import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { PageHeader } from "@/components/ui/page-header";

const LAST_UPDATED = "27 de abril de 2026";
const CONTROLLER_NAME = "Blackwise Tecnologia Ltda.";
const CONTROLLER_CNPJ = "58.153.615/0001-38";
const CONTROLLER_EMAIL = "cloud@blackwise.tech";
const DPO_EMAIL = "cloud@blackwise.tech";

/**
 * Privacy policy page mirrored from hotsite (/privacidade) for the
 * authenticated app. Same content, integrated with the in-app layout.
 *
 * If a shared @clinisharp/legal package is created later, this file
 * should re-export from there. For now it is duplicated by design.
 */
export function PrivacyPolicy() {
  return (
    <section className="space-y-7 pb-10">
      <PageHeader
        eyebrow="Jurídico"
        title="Política de Privacidade"
        lede={`Última atualização: ${LAST_UPDATED}`}
      />

      <article className="space-y-7 text-zinc-700 leading-relaxed">
        <p>
          Esta Política descreve como o CliniSharp, plataforma de gestão e
          inteligência artificial para clínicas, trata dados pessoais de
          visitantes, leads, clientes (clínicas contratantes) e usuários
          finais. Está em conformidade com a Lei 13.709/2018 (Lei Geral de
          Proteção de Dados &mdash; LGPD).
        </p>

        <Section title="1. Quem é o controlador dos dados">
          <p>
            O controlador dos dados pessoais é{" "}
            <strong>{CONTROLLER_NAME}</strong>, CNPJ {CONTROLLER_CNPJ}, com
            sede em Bauru &mdash; SP. Contato:{" "}
            <a
              className="text-teal-600 font-medium underline underline-offset-2"
              href={`mailto:${CONTROLLER_EMAIL}`}
            >
              {CONTROLLER_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="2. Encarregado pelo Tratamento de Dados (DPO)">
          <p>
            Para questões relacionadas a dados pessoais, contate nosso DPO
            pelo e-mail{" "}
            <a
              className="text-teal-600 font-medium underline underline-offset-2"
              href={`mailto:${DPO_EMAIL}`}
            >
              {DPO_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="3. Quais dados coletamos">
          <p className="font-semibold mt-2">3.1 Dados fornecidos por você</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Nome completo, e-mail, telefone e WhatsApp;</li>
            <li>Dados profissionais (clínica, CNPJ, especialidade, cargo);</li>
            <li>
              Dados de pagamento, processados por gateways parceiros (não
              armazenamos dados completos de cartão);
            </li>
            <li>Mensagens enviadas pelos formulários ou canais de atendimento.</li>
          </ul>

          <p className="font-semibold mt-4">3.2 Dados coletados automaticamente</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Endereço IP e geolocalização aproximada;</li>
            <li>Dados de navegação (páginas, tempo, origem);</li>
            <li>Identificadores de dispositivo, sistema e navegador;</li>
            <li>Cookies (essenciais e analíticos).</li>
          </ul>

          <p className="font-semibold mt-4">3.3 Dados de pacientes</p>
          <p className="mt-2">
            Quando uma clínica contrata o CliniSharp, ela é a controladora dos
            dados de seus pacientes e o CliniSharp atua como{" "}
            <strong>operador</strong>, processando esses dados conforme
            instruções da clínica.
          </p>
        </Section>

        <Section title="4. Para quais finalidades usamos seus dados">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Captação e qualificação de leads</strong>, marketing e
              comunicação;
            </li>
            <li>
              <strong>Prestação do serviço</strong>: criação de conta,
              pagamentos, suporte, operação do software;
            </li>
            <li>
              <strong>Segurança e prevenção de fraude</strong>: registros de
              acesso, detecção de anomalias;
            </li>
            <li>
              <strong>Melhoria contínua</strong>: análises agregadas de uso.
            </li>
          </ul>
        </Section>

        <Section title="5. Bases legais (art. 7º da LGPD)">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Consentimento</strong>: comunicações e cookies não
              essenciais.
            </li>
            <li>
              <strong>Execução de contrato</strong>: pagamentos, software,
              suporte.
            </li>
            <li>
              <strong>Cumprimento de obrigação legal</strong>: fiscal,
              tributária e regulatória.
            </li>
            <li>
              <strong>Legítimo interesse</strong>: segurança, antifraude,
              relacionamento com clientes.
            </li>
          </ul>
        </Section>

        <Section title="6. Com quem compartilhamos">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Análises</strong>: Google Analytics 4 e Google Tag
              Manager (mediante consentimento).
            </li>
            <li>
              <strong>Infraestrutura</strong>: AWS, Vercel, Google Cloud,
              CDNs.
            </li>
            <li>
              <strong>Comunicação</strong>: e-mail transacional, WhatsApp
              Business API, chat.
            </li>
            <li>
              <strong>Pagamentos</strong>: operadores de pagamento e gateways de
              cobrança.
            </li>
            <li>
              <strong>Autoridades públicas</strong>: somente mediante
              requisição legal.
            </li>
          </ul>
        </Section>

        <Section title="7. Cookies e tecnologias semelhantes">
          <p>Duas categorias:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Essenciais</strong> (sempre ativos): sessão, segurança,
              balanceamento.
            </li>
            <li>
              <strong>Analíticos</strong>: GA4 (após consentimento).
            </li>
          </ul>
        </Section>

        <Section title="8. Seus direitos como titular (art. 18 da LGPD)">
          <ul className="list-disc pl-5 space-y-1">
            <li>Confirmação e acesso aos seus dados;</li>
            <li>Correção, anonimização, bloqueio ou eliminação;</li>
            <li>Portabilidade dos dados;</li>
            <li>Revogação do consentimento a qualquer momento;</li>
            <li>Reclamação à ANPD.</li>
          </ul>
          <p className="mt-3">
            Para solicitar a exclusão dos seus dados, utilize a página{" "}
            <Link
              to="/settings/exclusao-de-dados"
              className="text-teal-600 underline underline-offset-2"
            >
              /settings/exclusao-de-dados
            </Link>{" "}
            ou escreva para{" "}
            <a
              className="text-teal-600 underline underline-offset-2"
              href={`mailto:${DPO_EMAIL}`}
            >
              {DPO_EMAIL}
            </a>
            . Responderemos em até 15 dias úteis.
          </p>
        </Section>

        <Section title="9. Período de retenção">
          <ul className="list-disc pl-5 space-y-1">
            <li>Leads não convertidos: até 24 meses.</li>
            <li>Clientes ativos: durante o contrato e 5 anos após o término.</li>
            <li>Dados financeiros: conforme prazos legais (tipicamente 5 anos).</li>
            <li>Logs de acesso: 6 meses (Marco Civil da Internet).</li>
            <li>Cookies: entre sessão e 13 meses.</li>
          </ul>
        </Section>

        <Section title="10. Segurança">
          <p>
            Adotamos medidas técnicas e organizacionais como criptografia em
            trânsito (TLS), em repouso, controle de acesso baseado em papéis,
            MFA para administradores, registros de auditoria e monitoramento
            contínuo.
          </p>
        </Section>

        <Section title="11. Transferência internacional">
          <p>
            Provedores fora do Brasil tratam dados apenas em países com nível
            adequado de proteção ou mediante cláusulas contratuais
            equivalentes à LGPD.
          </p>
        </Section>

        <Section title="12. Alterações desta Política">
          <p>
            Esta Política pode ser atualizada periodicamente. A versão vigente
            está sempre disponível nesta página.
          </p>
        </Section>

        <Section title="13. Contato">
          <p>
            <a
              className="text-teal-600 font-medium underline underline-offset-2"
              href={`mailto:${DPO_EMAIL}`}
            >
              {DPO_EMAIL}
            </a>
          </p>
        </Section>
      </article>
    </section>
  );
}

type SectionProps = { title: string; children: ReactNode };

function Section({ title, children }: SectionProps) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-zinc-800">
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
